import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getOnboardingFormData, updateOnboardingForm } from '../services/onboardingService';
import OnboardingForm from '../components/OnboardingFormNew';
import styles from '../styles/OnboardingForm.module.css';
import DataFallback from '../components/feedback/DataFallback';

export default function EditOnboardingFormPage() {
    const token = useSelector(state => state.user.token);
    const [initialData, setInitialData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isViewMode, setIsViewMode] = useState(true); // Start in view mode

    // Helper to flatten the API response to match form state
    const transformApiDataToFormState = (apiData) => {
        if (!apiData) return {};

        // Extract nested objects
        const {
            brand,
            contact_information,
            legal_financial,
            manufacturing,
            order_policy,
            product_categorization,
            warehouses,
            exchange_policy,
            return_policy
        } = apiData;

        const ci = contact_information || {};
        const lf = legal_financial || {};
        const mfg = manufacturing || {};
        const op = order_policy || {};
        const pc = product_categorization || {};
        const wh = (warehouses && warehouses.length > 0) ? warehouses[0] : {}; // Assume single warehouse for now
        const ep = exchange_policy || {};
        const rp = return_policy || {};

        return {
            // Brand Info
            brand_name: brand?.name || '',
            brand_logo_url: brand?.logo_url || '',

            // Contact Information (from contact_information object)
            brand_poc_name: ci.brand_poc_name || '',
            brand_poc_number: ci.brand_poc_number || '',
            email_address: ci.email_address || '',
            general_contact_number: ci.general_contact_number || '',

            // Company Info (from contact_information)
            company_name: ci.company_name || '',
            company_address: ci.company_address || '',
            billing_address: ci.billing_address || '',

            // Legal & Financial
            gstin: lf.gstin || '',
            bank_name: lf.bank_name || '',
            bank_account_no: lf.bank_account_no || '',
            bank_account_type: lf.bank_account_type || '',
            bank_ifsc: lf.bank_ifsc || '',
            account_holder_name: lf.account_holder_name || '',
            bank_branch: lf.bank_branch || '',
            gstin_certificate: lf.gstin_certificate_file || '',
            cancelled_cheque: lf.cancelled_cheque_file || '',
            // File URLs for display
            gstin_certificate_file_url: lf.gstin_certificate_file_url || '',
            cancelled_cheque_file_url: lf.cancelled_cheque_file_url || '',

            // Manufacturing
            manufacturing_country: mfg.manufacturing_country || '',
            manufacturer_name: mfg.manufacturer_name || '',
            manufacturer_address: mfg.manufacturer_address || '',
            packer_name: mfg.packer_name || '',
            packer_address: mfg.packer_address || '',
            shipper_name: mfg.shipper_name || '',
            shipper_address: mfg.shipper_address || '',
            packaging_materials_used: mfg.packaging_materials_used || '',
            importer_of_record: mfg.importer_of_record || '',
            import_license_number: mfg.import_license_number || '',
            manufacturer_excel: mfg.manufacturer_excel_file || '',
            // File URL for display
            manufacturer_excel_file_url: mfg.manufacturer_excel_file_url || '',
            // Derive checkbox state: if packer matches manufacturer
            manufacturing_and_packer_same: (mfg.manufacturer_name === mfg.packer_name && mfg.manufacturer_address === mfg.packer_address),

            // Order Policy
            country_of_origin: op.country_of_origin || '',
            general_processing_timeline: op.general_processing_timeline || '',
            dispatch_timelines: op.dispatch_timelines || '',
            made_to_order: op.made_to_order || false,

            // Product Categorization
            suspire_category: pc.suspire_category || '',
            sizing_chart_link: pc.sizing_chart_link || '',
            signed_agreement: pc.signed_agreement_file || '',
            certifications: pc.certifications || '',
            // File URLs for display
            signed_agreement_file_url: pc.signed_agreement_file_url || '',
            certifications_url: pc.certifications_url || '',

            // Warehouse / Operations POC
            warehouse_poc_name: wh.warehouse_poc_name || '',
            warehouse_poc_number: wh.warehouse_poc_number || '',
            warehouse_poc_address: wh.warehouse_poc_address || '',
            operations_poc_name: wh.operations_poc_name || '',
            operations_poc_number: wh.operations_poc_number || '',
            operations_poc_email: wh.operations_poc_email || '',

            // Policies - enable if they have content
            exchanges: Object.keys(ep).length > 0,
            returns: Object.keys(rp).length > 0,

            // Spread policy details flattened
            ...ep,
            ...rp,

            // Terms
            accepted_terms: true,
        };
    };

    useEffect(() => {
        async function fetchData() {
            try {
                const data = await getOnboardingFormData(token);
                const transformed = transformApiDataToFormState(data);
                setInitialData(transformed);
            } catch (err) {
                console.error('Failed to load onboarding form data', err);
                setError('Failed to load existing data. Please try again later.');
            } finally {
                setLoading(false);
            }
        }

        if (token) {
            fetchData();
        }
    }, [token]);

    const handleUpdate = async (formData) => {
        try {
            await updateOnboardingForm(token, formData);
            // Success is handled inside OnboardingForm or we can show a toast here
            // But OnboardingFormNew.js (as seen in codebase) handles submission logic internally mostly,
            // we need to see how we can inject the update logic.
            // Actually, OnboardingFormNew.js likely calls submitOnboardingForm directly.
            // We will need to modify OnboardingFormNew.js to accept an onSubmit prop or similar
            // to override the default behavior, OR pass a prop `isEditMode`.
        } catch (err) {
            throw err; // Propagate to form for error handling
        }
    };

    if (loading) return <div className={styles.container}>Loading form data...</div>;
    if (error) return (
      <div className={styles.container}>
        <DataFallback
          variant="pane"
          error={typeof error === 'string' ? { message: error, retryable: true } : error}
          onRetry={() => window.location.reload()}
        />
      </div>
    );

    const handleEdit = () => {
        setIsViewMode(false);
    };

    const handleCancel = () => {
        setIsViewMode(true);
        // Reset form to initial data will be handled by OnboardingForm component
    };

    const handleSave = async (formData) => {
        try {
            await updateOnboardingForm(token, formData);
            setIsViewMode(true);
            // Refresh data after save
            const data = await getOnboardingFormData(token);
            const transformed = transformApiDataToFormState(data);
            setInitialData(transformed);
        } catch (err) {
            throw err; // Propagate to form for error handling
        }
    };

    return (
        <div className={styles.container}>
            <OnboardingForm
                initialData={initialData}
                isEditMode={true}
                isViewMode={isViewMode}
                onEdit={handleEdit}
                onCancel={handleCancel}
                onSave={handleSave}
            />
        </div>
    );
}
